<template>
  <treeselect
    v-bind="$attrs"
    :options="processedOptions"
    v-on="listeners"
    @select="onSelect"
    @deselect="onDeselect"
  >
    <!-- Custom option label slot: show DELETED badge -->
    <span
      slot="option-label"
      slot-scope="{ node }"
      :style="{
        opacity: node.raw.deleted == 1 ? 0.5 : 1,
        cursor: node.raw.deleted == 1 ? 'not-allowed' : 'pointer',
      }"
    >
      {{ node.label }}
      <span
        v-if="node.raw.deleted == 1"
        style="
          color: red;
          font-weight: bold;
          font-size: 0.7em;
          text-transform: uppercase;
          margin-left: 18px;
        "
      >
        DELETED
      </span>
    </span>

    <!-- Custom value label slot: show DELETED badge in the input -->
    <div
      slot="value-label"
      slot-scope="{ node }"
      style="display: flex; align-items: center"
    >
      <span>{{ node.label }}</span>
      <span
        v-if="node.raw && node.raw.deleted == 1"
        style="
          color: red;
          font-weight: bold;
          font-size: 0.7em;
          text-transform: uppercase;
          margin-left: 6px;
        "
      >
        (DELETED)
      </span>
    </div>
  </treeselect>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'

export default {
  name: 'MasterTreeselect',
  components: {
    Treeselect,
  },
  inheritAttrs: false,
  data() {
    return {
      deletedIdSet: new Set(),
    }
  },
  computed: {
    processedOptions() {
      const options = this.$attrs.options || []
      this.deletedIdSet.clear()
      return this.processTree(options)
    },
    listeners() {
      // Remove select/deselect from parent listeners so we can intercept
      const { select, deselect, ...rest } = this.$listeners
      return rest
    },
  },
  methods: {
    processTree(nodes) {
      if (!nodes || !Array.isArray(nodes)) return []
      // Sort: non-deleted first, deleted last (preserve relative order)
      const sorted = [...nodes].sort(
        (a, b) => (a.deleted || 0) - (b.deleted || 0),
      )
      return sorted.map(node => this.processNode(node, false))
    },
    processNode(node, parentDeleted) {
      // eslint-disable-next-line eqeqeq
      const isDeleted = node.deleted == 1 || parentDeleted
      const processed = { ...node }

      // Check if all children are deleted — if so, treat this parent as deleted too
      const allChildrenDeleted = processed.children && processed.children.length && processed.children.every(child => child.deleted === 1)
      const effectivelyDeleted = isDeleted || allChildrenDeleted

      // Track deleted IDs so we can reject selection attempts
      if (effectivelyDeleted) {
        this.deletedIdSet.add(node.id)
      }

      // If this node is deleted (or all children deleted) and has children,
      // remove children to prevent expanding the tree
      if (
        effectivelyDeleted
        && processed.children
        && processed.children.length
      ) {
        delete processed.children
      } else if (processed.children && processed.children.length) {
        processed.children = processed.children.map(child => this.processNode(child, isDeleted))
      }

      return processed
    },
    onSelect(node) {
      // If a deleted item was just selected, immediately undo it
      // eslint-disable-next-line eqeqeq
      if (node.deleted == 1 || this.deletedIdSet.has(node.id)) {
        this.$nextTick(() => {
          const currentValue = this.$attrs.value
          if (Array.isArray(currentValue)) {
            const filtered = currentValue.filter(v => v !== node.id)
            this.$emit('input', filtered)
          }
        })
        return
      }
      // Forward the event to parent
      this.$emit('select', node)
    },
    onDeselect(node) {
      this.$emit('deselect', node)
    },
  },
}
</script>

<style>
/* Style deleted items in the dropdown to look disabled */
.vue-treeselect__option--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
